import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  isDevMode,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter, Router, withComponentInputBinding } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { createErrorHandler, TraceService } from '@sentry/angular';

import { routes } from './app.routes';
import { apiInterceptor } from './core/interceptors/api.interceptor';
import { AuthService } from './core/services/auth.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptors([apiInterceptor])),

    /**
     * PWA. `registerWhenStable` evita que el registro del service worker compita
     * con el arranque de la app (con tope de 30 s por si nunca se estabiliza).
     */
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),

    /**
     * Restaura la sesión antes de resolver la primera ruta.
     *
     * Sin esto, al recargar la página el `authGuard` correría con el usuario aún
     * vacío y mandaría al login a alguien que sí tiene un token válido.
     */
    provideAppInitializer(async () => {
      await inject(AuthService).restaurarSesion();
    }),

    /**
     * Sentry. El `ErrorHandler` reporta las excepciones que Angular no captura;
     * el `TraceService` convierte cada navegación del router en una transacción.
     * Hay que instanciarlo explícitamente al arrancar: nadie lo inyecta, así que
     * sin esto Angular nunca lo construiría y no se trazaría ninguna ruta.
     */
    {
      provide: ErrorHandler,
      useValue: createErrorHandler(),
    },
    {
      provide: TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(TraceService);
    }),
  ],
};
